import sendErrorReport from 'common/utils/sendErrorReport';

import { EMBEDDED_EVENTS, LOOKER_DOMAIN } from './constants';
import { DashboardRunStart, DashboardRunComplete, EmbeddedEvent, DashboardLoaded } from './types';
import { telemetryLoadLookerDashboardDuration, telemetryLoadLookerDashboardError } from '../telemetry';

export const isValidLookerEvent = (event: MessageEvent, contentWindow?: Window | null | undefined) => {
  try {
    JSON.parse(event.data);
  } catch (_) {
    // We're relying on the event data to know if there is any error when loading the dashboard,
    // but if the event data is not a valid JSON string,
    // we also don't want to report unhandled error due to the failure of parsing.
    return false;
  }
  return event.source === contentWindow && event.origin === LOOKER_DOMAIN;
};

const trackDashboardLoadStart = (embeddedEvent: DashboardRunStart) =>
  performance.mark(`dashboard-${embeddedEvent.dashboard.id}-${EMBEDDED_EVENTS.DASHBOARD_RUN_START}`);

const trackDashboardLoadComplete = (embeddedEvent: DashboardRunComplete) => {
  performance.mark(`dashboard-${embeddedEvent.dashboard.id}-${EMBEDDED_EVENTS.DASHBOARD_RUN_COMPLETE}`);
  const loadDashboardMeasure = performance.measure(
    `load-dashboard-${embeddedEvent.dashboard.id}-measure`,
    `dashboard-${embeddedEvent.dashboard.id}-${EMBEDDED_EVENTS.DASHBOARD_RUN_START}`,
    `dashboard-${embeddedEvent.dashboard.id}-${EMBEDDED_EVENTS.DASHBOARD_RUN_COMPLETE}`,
  );
  const roundedDurationMillisecond = Math.round(loadDashboardMeasure.duration);
  telemetryLoadLookerDashboardDuration(roundedDurationMillisecond);
};

const handleTileErrors = (embeddedEvent: DashboardRunComplete) => {
  // If the dashboard is loaded correctly but any tile inside the dashboard has query errors,
  // we don't have to show the full-page error state because some tiles might still be able to load.
  const dashboardTileStatuses = embeddedEvent.tileStatuses
    ?.map(
      tileStatus =>
        `Tile ID: ${tileStatus.tileId}, Tile Status: ${tileStatus.status}${
          tileStatus.status === 'error' && tileStatus.errors
            ? `, Error messages: ${tileStatus.errors.map(error => error.message).join(', ')}`
            : ''
        }`,
    )
    .join(';');
  sendErrorReport(
    new Error(
      `Failed to query some tiles in ${embeddedEvent.dashboard.title} dashboard: ${embeddedEvent.dashboard.absoluteUrl}. Tile statuses: ${dashboardTileStatuses}`,
    ),
    'historical-venue-availability-looker',
  );
  telemetryLoadLookerDashboardError();
};

const handleDashboardLoadError = (embeddedEvent: DashboardLoaded, onError: Function) => {
  onError();
  sendErrorReport(
    new Error(`Failed to load ${embeddedEvent.dashboard.title} dashboard: ${embeddedEvent.dashboard.absoluteUrl}`),
    'historical-venue-availability-looker',
  );
  telemetryLoadLookerDashboardError();
};

export const handleLookerEvent = (embeddedEvent: EmbeddedEvent, onError: Function) => {
  switch (embeddedEvent.type) {
    case EMBEDDED_EVENTS.DASHBOARD_LOADED:
      const hasDashboardError = embeddedEvent.status === 'error';
      return hasDashboardError && handleDashboardLoadError(embeddedEvent, onError);
    case EMBEDDED_EVENTS.DASHBOARD_RUN_COMPLETE:
      trackDashboardLoadComplete(embeddedEvent);
      const hasTileErrors = embeddedEvent.tileStatuses?.some(tileStatus => tileStatus.status === 'error');
      return hasTileErrors && handleTileErrors(embeddedEvent);
    case EMBEDDED_EVENTS.DASHBOARD_RUN_START:
      trackDashboardLoadStart(embeddedEvent);
      break;
  }
};
